import type { PilotId } from '~/eds/src/types/PilotId';

import {
  parseDocumentsTable,
  type PersonDocumentsTable,
} from '../documents/homeDocuments';
import {
  MAX_SUGGESTED_QUESTION_LENGTH,
  MAX_SUGGESTED_QUESTIONS,
  type HomeSuggestedQuestion,
} from './suggestedQuestions';

// Find-key for the reserved private saved search, not UI copy — intentionally not localized.
export const RESERVED_SCOPE_NAME = 'My Home scope';

/**
 * A structural, non-recursive view of `EntityQuery`, which every `EntityQuery`
 * satisfies. Widened because `SectionEntity` nests itself and HomeSettings is
 * what TanStack Form derives key paths from in HomeSettingsPanel, so a leaf
 * typed `EntityQuery[]` fails to compile with TS2589.
 */
export interface HomeScopeQueryEntity {
  type: string;
  id?: unknown;
  operator?: unknown;
  value?: unknown;
}

export type HomeScopeQuery = HomeScopeQueryEntity[];

export interface HomeScopePref {
  /** Pointer to the reserved saved search; kept even by a Clear. */
  savedSearchId: PilotId | null;
  name: string;
  /** The user's own words. Lives only here, never on the saved search. */
  description: string;
  /** Evisearch form, unconverted, so Search opens the scope the queue runs. */
  query: HomeScopeQuery;
}

export type HomeSectionId = 'recentlyViewed' | 'yourWork';

interface HomeSectionsSettings {
  order: HomeSectionId[];
  visible: Record<HomeSectionId, boolean>;
}

interface HomeWorkTypeSettings {
  renewals: boolean;
  autoRenewals: boolean;
  assignedTasks: boolean;
  waitingOnOthers: boolean;
}

interface HomeDeadlineSettings {
  renewLeadDays: number;
  nonRenewalLeadDays: number;
  requestDueDefaultDays: number;
}

export type HomeOverdueMode = 'all' | 'age' | 'none';

interface HomeOverdueSettings {
  mode: HomeOverdueMode;
  days: number;
}

// Named after the real card-action identifiers the Your Work queue already
// consumes (QueueSettings in ui#6981, src/features/home/shared/queueSettings.ts)
// so the two settings shapes can be reconciled later without a field-by-field
// remap.
interface HomeActionSettings {
  renew: boolean;
  send_non_renewal_notice: boolean;
  terminate: boolean;
  needs_approval: boolean;
  send_for_signature: boolean;
  act_on_task: boolean;
  nudge_internal: boolean;
  waiting_on_counterparty: boolean;
}

export interface HomeSettings {
  sections: HomeSectionsSettings;
  workTypes: HomeWorkTypeSettings;
  deadlines: HomeDeadlineSettings;
  overdue: HomeOverdueSettings;
  actions: HomeActionSettings;
  // Empty means "never customized" -- a save never persists zero questions,
  // so an empty array unambiguously means the curated defaults still apply.
  // Those defaults are localized (getDefaultSuggestedQuestions needs an
  // IntlShape), so they can't live in this static DEFAULT_HOME_SETTINGS --
  // useHomeSuggestedQuestions supplies them at read time instead.
  pinnedQuestions: HomeSuggestedQuestion[];
  scope: HomeScopePref | null;
  /** `null` or absent means the product default. */
  documents?: PersonDocumentsTable | null;
}

export const DEFAULT_HOME_SETTINGS: HomeSettings = {
  sections: {
    order: ['recentlyViewed', 'yourWork'],
    visible: { recentlyViewed: true, yourWork: true },
  },
  workTypes: {
    renewals: true,
    autoRenewals: true,
    assignedTasks: true,
    waitingOnOthers: true,
  },
  deadlines: {
    renewLeadDays: 90,
    nonRenewalLeadDays: 30,
    requestDueDefaultDays: 15,
  },
  overdue: { mode: 'age', days: 90 },
  pinnedQuestions: [],
  scope: null,
  documents: null,
  actions: {
    renew: true,
    send_non_renewal_notice: true,
    terminate: true,
    needs_approval: true,
    send_for_signature: true,
    act_on_task: true,
    nudge_internal: true,
    waiting_on_counterparty: true,
  },
};

const OVERDUE_MODES: HomeOverdueMode[] = ['all', 'age', 'none'];

function mergeBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function mergeNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function mergeSections(
  value: unknown,
  fallback: HomeSectionsSettings,
): HomeSectionsSettings {
  const candidate = asRecord(value);

  // Keep whichever known, non-duplicate ids the user actually ordered, then
  // append any id that's in the current default order but wasn't in theirs
  // -- covers both garbage entries and a section added after they last
  // saved, without discarding their whole custom order over either.
  const candidateOrder = candidate.order;
  const knownOrder = Array.isArray(candidateOrder)
    ? candidateOrder.filter(
        (id, index, self): id is HomeSectionId =>
          fallback.order.includes(id as HomeSectionId) &&
          self.indexOf(id) === index,
      )
    : [];
  const newIds = fallback.order.filter((id) => !knownOrder.includes(id));
  const order =
    knownOrder.length > 0 ? [...knownOrder, ...newIds] : fallback.order;

  const candidateVisible = asRecord(candidate.visible);
  const visible = fallback.order.reduce<Record<HomeSectionId, boolean>>(
    (acc, id) => {
      acc[id] = mergeBoolean(candidateVisible[id], fallback.visible[id]);
      return acc;
    },
    {} as Record<HomeSectionId, boolean>,
  );

  return { order, visible };
}

function mergeWorkTypes(
  value: unknown,
  fallback: HomeWorkTypeSettings,
): HomeWorkTypeSettings {
  const candidate = asRecord(value);
  const renewals = mergeBoolean(candidate.renewals, fallback.renewals);
  const autoRenewals = mergeBoolean(
    candidate.autoRenewals,
    fallback.autoRenewals,
  );

  // `waitingOnOthers` is the discriminator for the two-leaf schema. A record
  // that already has it must not be re-collapsed from retired `approvals` /
  // `signatures` keys that may still be sitting next to it until the next save.
  if (typeof candidate.waitingOnOthers === 'boolean') {
    return {
      renewals,
      autoRenewals,
      assignedTasks: mergeBoolean(
        candidate.assignedTasks,
        fallback.assignedTasks,
      ),
      waitingOnOthers: candidate.waitingOnOthers,
    };
  }

  const hasLegacyLeaf = ['approvals', 'signatures', 'assignedTasks'].some(
    (key) => typeof candidate[key] === 'boolean',
  );
  if (!hasLegacyLeaf) {
    return {
      renewals,
      autoRenewals,
      assignedTasks: fallback.assignedTasks,
      waitingOnOthers: fallback.waitingOnOthers,
    };
  }

  const approvals = mergeBoolean(candidate.approvals, true);
  const signatures = mergeBoolean(candidate.signatures, true);
  const assignedTasks = mergeBoolean(candidate.assignedTasks, true);
  return {
    renewals,
    autoRenewals,
    assignedTasks: approvals || signatures || assignedTasks,
    waitingOnOthers: fallback.waitingOnOthers,
  };
}

function mergeDeadlines(
  value: unknown,
  fallback: HomeDeadlineSettings,
): HomeDeadlineSettings {
  const candidate = asRecord(value);
  return {
    renewLeadDays: mergeNumber(candidate.renewLeadDays, fallback.renewLeadDays),
    nonRenewalLeadDays: mergeNumber(
      candidate.nonRenewalLeadDays,
      fallback.nonRenewalLeadDays,
    ),
    requestDueDefaultDays: mergeNumber(
      candidate.requestDueDefaultDays,
      fallback.requestDueDefaultDays,
    ),
  };
}

function mergeOverdue(
  value: unknown,
  fallback: HomeOverdueSettings,
): HomeOverdueSettings {
  const candidate = asRecord(value);
  const mode = OVERDUE_MODES.includes(candidate.mode as HomeOverdueMode)
    ? (candidate.mode as HomeOverdueMode)
    : fallback.mode;
  return { mode, days: mergeNumber(candidate.days, fallback.days) };
}

function mergeActions(
  value: unknown,
  fallback: HomeActionSettings,
): HomeActionSettings {
  const candidate = asRecord(value);
  return {
    renew: mergeBoolean(candidate.renew, fallback.renew),
    send_non_renewal_notice: mergeBoolean(
      candidate.send_non_renewal_notice,
      fallback.send_non_renewal_notice,
    ),
    terminate: mergeBoolean(candidate.terminate, fallback.terminate),
    needs_approval: mergeBoolean(
      candidate.needs_approval,
      fallback.needs_approval,
    ),
    send_for_signature: mergeBoolean(
      candidate.send_for_signature,
      fallback.send_for_signature,
    ),
    act_on_task: mergeBoolean(candidate.act_on_task, fallback.act_on_task),
    nudge_internal: mergeBoolean(
      candidate.nudge_internal,
      fallback.nudge_internal,
    ),
    waiting_on_counterparty: mergeBoolean(
      candidate.waiting_on_counterparty,
      fallback.waiting_on_counterparty,
    ),
  };
}

function isHomeSuggestedQuestion(
  value: unknown,
): value is HomeSuggestedQuestion {
  const candidate = asRecord(value);
  return (
    typeof candidate.id === 'string' &&
    candidate.id.length > 0 &&
    typeof candidate.text === 'string'
  );
}

// Trims, caps, drops blanks, and de-dupes by id. This merge layer only
// needs to defend against untrusted/stale stored JSON, not re-derive
// those invariants.
function mergePinnedQuestions(
  value: unknown,
  fallback: HomeSuggestedQuestion[],
): HomeSuggestedQuestion[] {
  if (!Array.isArray(value)) {
    return fallback;
  }
  const questions = value
    .filter(isHomeSuggestedQuestion)
    .map((question) => ({
      ...question,
      text: question.text.trim().slice(0, MAX_SUGGESTED_QUESTION_LENGTH),
    }))
    // Blanks must be dropped before de-duping -- otherwise a blank entry
    // sharing an id with a later legitimate one would still occupy that id's
    // first-occurrence slot and wrongly take the legitimate entry down with it.
    .filter((question) => question.text.length > 0)
    .filter(
      (question, index, self) =>
        self.findIndex((candidate) => candidate.id === question.id) === index,
    );
  return questions.length > 0
    ? questions.slice(0, MAX_SUGGESTED_QUESTIONS)
    : fallback;
}

// A malformed scope is no scope, never a failed settings read: it comes back
// from arbitrary server-side JSON alongside every other leaf, so throwing would
// take the user's sections, deadlines and pinned questions down with it.
//
// An incoming `matchCount` is ignored rather than rejected: v1 drops the field,
// but failing the leaf over it would strand every prototype-era blob, which has
// no version marker to migrate from.
function mergeScope(value: unknown) {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const candidate = value as Record<string, unknown>;

  const { query, savedSearchId, description, name } = candidate;
  if (
    !Array.isArray(query) ||
    query.some(
      (entity) =>
        typeof entity !== 'object' || entity === null || Array.isArray(entity),
    )
  ) {
    return null;
  }
  const isId =
    (typeof savedSearchId === 'string' && savedSearchId !== '') ||
    (typeof savedSearchId === 'number' && Number.isFinite(savedSearchId));
  if (savedSearchId !== undefined && savedSearchId !== null && !isId) {
    return null;
  }

  return {
    savedSearchId: isId ? savedSearchId : null,
    name: typeof name === 'string' && name ? name : RESERVED_SCOPE_NAME,
    description: typeof description === 'string' ? description : '',
    query: query as HomeScopeQuery,
  } satisfies HomeScopePref;
}

// The `homeSettings` preference key stores arbitrary JSON server-side, so a
// fetched value could be a stale write from before a schema change, or (as
// happened against a shared dev account) a different feature's payload
// entirely. Merges onto DEFAULT_HOME_SETTINGS field by field rather than an
// all-or-nothing shape check, so a value that's ours but partially malformed
// only loses the specific leaves that are missing or the wrong type.
export function mergeHomeSettings(
  value: unknown,
  fallback: HomeSettings = DEFAULT_HOME_SETTINGS,
): HomeSettings {
  const candidate = asRecord(value);
  return {
    sections: mergeSections(candidate.sections, fallback.sections),
    workTypes: mergeWorkTypes(candidate.workTypes, fallback.workTypes),
    deadlines: mergeDeadlines(candidate.deadlines, fallback.deadlines),
    overdue: mergeOverdue(candidate.overdue, fallback.overdue),
    pinnedQuestions: mergePinnedQuestions(
      candidate.pinnedQuestions,
      fallback.pinnedQuestions,
    ),
    actions: mergeActions(candidate.actions, fallback.actions),
    scope:
      candidate.scope === undefined
        ? fallback.scope
        : mergeScope(candidate.scope),
    documents:
      candidate.documents === undefined
        ? fallback.documents
        : parseDocumentsTable(candidate.documents),
  };
}

export function moveHomeSection(
  order: HomeSectionId[],
  index: number,
  direction: -1 | 1,
): HomeSectionId[] {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= order.length) {
    return order;
  }
  const next = [...order];
  [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
  return next;
}
